import type { UIEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { List, type RowComponentProps } from 'react-window';
import { BulkInput } from '../bulk/BulkInput';
import { Item } from '../components/Item';
import { StyledListItemText } from '../components/ListItemText.styled';
import { StyledMuiList } from '../components/MuiList.styled';
import { StyledVirtualListContainerDiv } from '../components/VirtualListContainerDiv.styled';
import type {
  BaseListOption,
  BulkInputUnionProps,
  CustomItemRender,
  EnrichedListOption,
  ItemUnionTypeProps,
  VirtualiedListUnionTypeProps,
} from '../types';
import { getPaddedRowHeight, offsetListRowStyle } from '../utils/utils';
import { BULK_BTN_SIZE, FOOTER_HEIGHT, ITEM_SIZE, LIST_HEIGHT, OVERSCAN_EXTRA_ITEMS } from './constants';

export type ListScrollOffsetProps = {
  scrollOffset: number;
};

export type VirtualizedFlatListProps<T> = {
  data: T[];
  searchInput: string;
  isDisabled: boolean;
  handleRowClick: (id: string) => void;
  handleItemRowClick?: (item: T) => void;
  isAsyncSearch: boolean;
  customEmptyText?: string;
  errorMessage?: string;
  hideActionIcon?: boolean;
  itemSize?: number;
  customItem?: CustomItemRender<T>;
  hasFooter?: boolean;
  onScroll?: (props: ListScrollOffsetProps) => void;
} & VirtualiedListUnionTypeProps &
  BulkInputUnionProps;

type FlatListRowProps = {
  data: EnrichedListOption<BaseListOption>[];
  searchInput: string;
  isDisabled: boolean;
  handleRowClick: (id: string) => void;
  handleItemRowClick?: (item: EnrichedListOption<BaseListOption>) => void;
  hideActionIcon?: boolean;
  customItem?: CustomItemRender<EnrichedListOption<BaseListOption>>;
  itemUnionProps: ItemUnionTypeProps;
  isSelectedList: boolean;
  itemSize: number;
};

const getFlatListRowHeight = (index: number, { itemSize, data }: FlatListRowProps) =>
  getPaddedRowHeight(itemSize, index, data.length);

const getFlatListRowKey = (index: number, { data }: FlatListRowProps) => data[index].id;

function FlatListRow({
  index,
  style,
  data,
  searchInput,
  isDisabled,
  handleRowClick,
  handleItemRowClick,
  hideActionIcon,
  customItem,
  itemUnionProps,
  isSelectedList,
  itemSize,
}: RowComponentProps<FlatListRowProps>) {
  const value = data[index];
  const rowStyle = offsetListRowStyle(style, itemSize);

  if (customItem) {
    return (
      <>
        {customItem({
          item: value,
          style: rowStyle,
          isDisabled: !!(value.isOnList && !isSelectedList),
          searchInput,
          handleRowClick: handleItemRowClick as (item: EnrichedListOption<BaseListOption>) => void,
          hideActionIcon,
        })}
      </>
    );
  }

  return (
    <Item
      {...itemUnionProps}
      handleRowClick={handleRowClick}
      hideActionIcon={hideActionIcon}
      id={value.id}
      isDisabled={isDisabled}
      isHeaderList={false}
      isFrozen={value.isFrozen}
      isOnList={value.isOnList}
      name={value.name}
      searchInput={searchInput}
      style={rowStyle}
    />
  );
}

export const VirtualizedFlatList = <T extends EnrichedListOption<BaseListOption>>(
  props: VirtualizedFlatListProps<T>
) => {
  const {
    data,
    searchInput,
    isSelectedList,
    handleRowClick,
    handleItemRowClick,
    isDisabled,
    isAsyncSearch,
    customEmptyText,
    hasBulkInput,
    errorMessage,
    customItem,
    hasFooter,
    onScroll,
  } = props;

  const { t } = useTranslation();

  const itemUnionProps: ItemUnionTypeProps = isSelectedList
    ? {
        actionType: props.actionType,
        isSelectedList: true,
      }
    : { isSelectedList: false };

  let listHeight = hasBulkInput ? LIST_HEIGHT - BULK_BTN_SIZE : LIST_HEIGHT;
  if (hasFooter) {
    listHeight -= FOOTER_HEIGHT;
  }

  const listItemText =
    customEmptyText ?? (isAsyncSearch ? t('Start to type in order to search for items.') : t('No items.'));

  const itemSize = props.itemSize || ITEM_SIZE;

  const handleListScroll = (event: UIEvent<HTMLDivElement>) => {
    onScroll?.({ scrollOffset: event.currentTarget.scrollTop });
  };

  return (
    <>
      {data.length ? (
        <StyledVirtualListContainerDiv hasError={!!errorMessage}>
          <List
            data-test="items-list"
            rowComponent={FlatListRow}
            rowCount={data.length}
            rowProps={{
              data,
              searchInput,
              isDisabled,
              handleRowClick,
              handleItemRowClick: handleItemRowClick as
                | ((item: EnrichedListOption<BaseListOption>) => void)
                | undefined,
              hideActionIcon: props.hideActionIcon,
              customItem: customItem as CustomItemRender<EnrichedListOption<BaseListOption>> | undefined,
              itemUnionProps,
              isSelectedList,
              itemSize,
            }}
            rowHeight={getFlatListRowHeight}
            rowKey={getFlatListRowKey}
            overscanCount={OVERSCAN_EXTRA_ITEMS}
            style={{ height: listHeight, width: '100%' }}
            onScroll={onScroll ? handleListScroll : undefined}
          />
        </StyledVirtualListContainerDiv>
      ) : (
        <StyledMuiList component="div" role="list" listHeight={listHeight} hasError={!!errorMessage}>
          <StyledListItemText
            marginLeft={16}
            marginRight={16}
            data-test="basic-searching-text"
            primary={listItemText}
            isSelected={true}
            disableTypography
          />
        </StyledMuiList>
      )}
      {hasBulkInput && (
        <BulkInput validate={props.handleBulkValidation} onAdd={props.handleBulkAdd} isDisabled={isDisabled} />
      )}
    </>
  );
};
